import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { EMPTY, Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, switchMap } from "rxjs/operators";

import { EvaService } from "src/app/core/services/eva.service";
import { EvaConfirmService } from "src/app/shared/ds/eva-confirm-dialog/eva-confirm.service";

import {
  Task,
  TaskAssignee,
  TaskObjectLink,
  TaskStatus,
  TaskStatusLabels,
  TaskUrgency,
  TaskUrgencyLabels,
  getAssigneeName,
} from "../domains/task";
import { EVA_OBJECT_TAGS, TagPreset } from "src/app/shared/constants/tag-presets";
import { EvaMenuItem } from "src/app/shared/ds/eva-menu/eva-menu.component";
import { SelectOption, SelectOptionTag } from "src/app/shared/ds/eva-select/eva-select.component";
import { TaskCategory } from "../domains/task-category";
import { TaskService } from "../services/task.service";
import { AssigneeItem } from "../task-assignee-picker/task-assignee-picker.component";
import { NoteService } from "src/app/services/note.service";
import { AttachmentSearchGroup } from "src/app/domains/note";

const OBJECT_TYPE_TO_EVA: Record<string, string> = {
  CUSTOMER: "CLIENT",
  PROJECT: "CHANTIER",
  OFFER: "OFFRE",
  INVOICE: "FACTURE_CLIENT",
  EMPLOYEE: "COLLABORATEUR",
  SUPPLIER: "FOURNISSEUR",
  PURCHASE_ORDER: "BON_COMMANDE",
  SUPPLIER_INVOICE: "FACTURE_ACHAT",
  INTERVENTION: "INTERVENTION",
  CONTRACT: "CONTRAT",
  CUSTOMER_SITE: "SITE",
  CATALOG: "CATALOGUE",
  ARTICLE: "ARTICLE",
  EXPENSE_REPORT: "NOTE_FRAIS",
};

const STATUS_TAG_SEVERITY: Record<TaskStatus, SelectOptionTag["severity"]> = {
  [TaskStatus.TODO]: "info",
  [TaskStatus.IN_PROGRESS]: "warning",
  [TaskStatus.DONE]: "success",
  [TaskStatus.CANCELLED]: "secondary",
};

@Component({
  selector: "app-task-detail-dialog",
  templateUrl: "./task-detail-dialog.component.html",
  styleUrls: ["./task-detail-dialog.component.scss"],
})
export class TaskDetailDialogComponent implements OnInit, OnChanges {
  private destroyRef = inject(DestroyRef);

  @Input() task: Task = null;
  @Input() visible = false;
  @Input() tasks: Task[] = [];
  @Input() employees: any[] = [];
  @Input() categories: TaskCategory[] = [];
  /** Liens objet non supprimables (ex : chantier courant dans le contexte projet) */
  @Input() lockedObjectLinks: TaskObjectLink[] = [];

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() taskUpdated = new EventEmitter<Task>();
  @Output() taskDeleted = new EventEmitter<string>();

  // Editable fields
  editTitle = "";
  editDescription = "";
  editUrgency: TaskUrgency = TaskUrgency.NORMAL;
  editStatus: TaskStatus = TaskStatus.TODO;
  editDueDate: Date = null;
  editPlannedStartDate: Date = null;
  editIsPrivate = false;
  editAssignedTo: AssigneeItem[] = [];
  editCategoryIds: string[] = [];
  categoryOptions: any[] = [];

  // Mode de date : aucune / échéance / période
  dateMode: "none" | "deadline" | "range" = "none";

  readonly dateModeOptions = [
    { label: "Aucune", value: "none" },
    { label: "Échéance", value: "deadline" },
    { label: "Période", value: "range" },
  ];

  // Lier à — recherche d'objets
  showLinkSearch = false;
  linkSearchQuery = "";
  linkSearchResults: AttachmentSearchGroup[] = [];
  isSearchingLinks = false;
  private linkSearch$ = new Subject<string>();

  // Auto-save subjects
  private titleChange$ = new Subject<string>();
  private descriptionChange$ = new Subject<string>();

  // Title inline edit
  titleEditing = false;

  // Navigation
  currentIndex = -1;

  // Completion animation
  celebrating = false;

  // Menu contextuel
  contextMenuItems: EvaMenuItem[] = [];

  // Options
  statusOptions: SelectOption[] = Object.keys(TaskStatus).map((key) => ({
    label: TaskStatusLabels[key as TaskStatus],
    value: key,
    tag: { label: TaskStatusLabels[key as TaskStatus], severity: STATUS_TAG_SEVERITY[key as TaskStatus] },
  }));

  urgencyOptions = Object.keys(TaskUrgency).map((key) => ({
    label: TaskUrgencyLabels[key as TaskUrgency],
    value: key,
    icon: this.getUrgencyIconForOption(key),
  }));

  private elementRef = inject(ElementRef);
  private noteService = inject(NoteService);

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: Event): void {
    if (this.showLinkSearch && !this.elementRef.nativeElement.contains(event.target)) {
      this.showLinkSearch = false;
      this.linkSearchQuery = "";
      this.linkSearchResults = [];
    }
  }

  constructor(
    private taskService: TaskService,
    public evaService: EvaService,
    private confirmationService: EvaConfirmService,
  ) {}

  ngOnInit() {
    this.initAutoSave();
    this.initLinkSearch();
    this.buildContextMenu();
    if (this.task) {
      this.loadTaskData();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["task"] && this.task) {
      this.loadTaskData();
      this.updateNavigationIndex();
    }
    if (changes["categories"] || changes["employees"]) {
      this.buildOptions();
    }
    if (changes["tasks"]) {
      this.updateNavigationIndex();
    }
  }

  private loadTaskData() {
    this.editTitle = this.task.title;
    this.editDescription = this.task.description || "";
    this.editCategoryIds = [...(this.task.category_ids || [])];
    this.editUrgency = this.task.urgency;
    this.editStatus = this.task.status;
    this.editDueDate = this.task.due_date;
    this.editPlannedStartDate = this.task.planned_start_date;
    this.editIsPrivate = this.task.is_private;
    // Convertir TaskAssignee en AssigneeItem (avec name obligatoire)
    this.editAssignedTo = (this.task.assigned_to || []).map((a) => ({
      type: a.type,
      employee_id: a.employee_id,
      name: a.name || this.getEmployeeName(a.employee_id) || "",
    }));
    this.titleEditing = false;
    this.celebrating = false;
    this.dateMode = this.resolveDateMode(this.editPlannedStartDate, this.editDueDate);
  }

  /** Déduit le mode date depuis les champs existants */
  private resolveDateMode(start: Date | null, end: Date | null): "none" | "deadline" | "range" {
    if (start != null && end != null) return "range";
    if (end != null) return "deadline";
    return "none";
  }

  /** Récupère le nom d'un employé par son ID */
  private getEmployeeName(employeeId?: string): string {
    if (!employeeId || !this.employees) return "";
    const emp = this.employees.find((e: any) => e.employee_id === employeeId);
    return emp ? `${emp.first_name} ${emp.last_name}` : "";
  }

  private buildOptions() {
    this.categoryOptions = this.categories.map((c) => ({
      label: c.name,
      value: c.category_id,
      color: c.color,
    }));
  }

  private buildContextMenu() {
    this.contextMenuItems = [
      {
        label: "Supprimer la tâche",
        icon: "heroTrash",
        severity: "danger",
        command: () => this.confirmDelete(),
      },
    ];
  }

  // --- Auto-save (debounce 500ms) ---
  private initAutoSave() {
    this.titleChange$
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((title) => this.taskService.updateTask(this.task.task_id, { title: title.trim() })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (updated) => this.onAutoSaveSuccess(updated),
        error: () => this.evaService.showError("Erreur lors de la sauvegarde du titre", "autoSaveTitle", ""),
      });

    this.descriptionChange$
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((description) => this.taskService.updateTask(this.task.task_id, { description })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (updated) => this.onAutoSaveSuccess(updated),
        error: () =>
          this.evaService.showError("Erreur lors de la sauvegarde de la description", "autoSaveDescription", ""),
      });
  }

  private onAutoSaveSuccess(updated: Task) {
    this.taskUpdated.emit(updated);
  }

  // --- Title ---
  onTitleChange() {
    if (this.editTitle.trim()) {
      this.titleChange$.next(this.editTitle);
    }
  }

  onTitleBlur() {
    this.titleEditing = false;
    if (!this.editTitle.trim()) {
      this.editTitle = this.task.title; // Restaurer si vide
    }
  }

  startTitleEdit() {
    this.titleEditing = true;
  }

  // --- Description ---
  onDescriptionChange() {
    this.descriptionChange$.next(this.editDescription);
  }

  // --- Field updates (immediate save) ---
  onCategoryIdsChange(categoryIds: string[]) {
    this.editCategoryIds = categoryIds;
    this.saveField({ category_ids: categoryIds });
  }

  onCategoryCreated(category: any) {
    // Recharger les catégories depuis le parent si nécessaire
    // Le picker gère déjà l'ajout à la sélection
  }

  onUrgencyChange() {
    this.saveField({ urgency: this.editUrgency });
  }

  onStatusChange() {
    this.saveField({ status: this.editStatus });
  }

  onDueDateChange() {
    this.saveField({ due_date: this.editDueDate });
  }

  onDateModeChange(mode: "none" | "deadline" | "range") {
    this.dateMode = mode;
    if (mode === "none") {
      this.editDueDate = null;
      this.editPlannedStartDate = null;
      this.saveField({ due_date: null, planned_start_date: null });
    } else if (mode === "deadline") {
      this.editPlannedStartDate = null;
      this.saveField({ planned_start_date: null });
    }
  }

  onDeadlineDateChange(value: Date | Date[] | null) {
    this.editDueDate = value instanceof Date ? value : null;
    this.saveField({ due_date: this.editDueDate });
  }

  onRangeDateChange(value: Date | Date[] | null) {
    if (Array.isArray(value)) {
      this.editPlannedStartDate = value[0] || null;
      this.editDueDate = value[1] || null;
    } else {
      this.editPlannedStartDate = null;
      this.editDueDate = null;
    }
    this.saveField({ planned_start_date: this.editPlannedStartDate, due_date: this.editDueDate });
  }

  get rangeDateValue(): Date[] | null {
    if (this.editPlannedStartDate || this.editDueDate) {
      return [this.editPlannedStartDate, this.editDueDate].filter(Boolean) as Date[];
    }
    return null;
  }

  onPrivateChange() {
    this.saveField({ is_private: this.editIsPrivate });
  }

  private saveField(payload: any) {
    if (!this.task?.task_id) return;
    this.taskService
      .updateTask(this.task.task_id, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => this.onAutoSaveSuccess(updated),
        error: () => this.evaService.showError("Erreur lors de la mise à jour", "saveField", ""),
      });
  }

  // --- Assignees ---
  onAssigneesChange(assignees: AssigneeItem[]) {
    this.editAssignedTo = assignees;
    this.saveAssignees();
  }

  private saveAssignees() {
    const payload = this.editAssignedTo.map((a) => {
      if (a.type === "employee") {
        return { type: "employee", employee_id: a.employee_id };
      }
      return { type: "external", name: a.name };
    });
    this.taskService
      .updateTaskAssignment(this.task.task_id, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => this.onAutoSaveSuccess(updated),
        error: () => this.evaService.showError("Erreur lors de la mise à jour des assignations", "saveAssignees", ""),
      });
  }

  // --- Completion ---
  markAsDone() {
    if (this.task.status === "DONE") return;
    this.celebrating = true;

    this.taskService
      .updateTaskStatus(this.task.task_id, "DONE")
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.evaService.showSuccess("Tâche terminée !");
          this.taskUpdated.emit(updated);
          setTimeout(() => {
            this.celebrating = false;
            this.close();
          }, 800);
        },
        error: () => {
          this.celebrating = false;
          this.evaService.showError("Erreur lors de la complétion", "markAsDone", "");
        },
      });
  }

  // --- Delete ---
  confirmDelete() {
    this.confirmationService.confirm({
      message: "Êtes-vous sûr de vouloir supprimer cette tâche ?",
      header: "Confirmation de suppression",
      acceptLabel: "Supprimer",
      rejectLabel: "Annuler",
      severity: "danger",
      accept: () => this.deleteTask(),
    });
  }

  private deleteTask() {
    this.taskService
      .deleteTask(this.task.task_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.evaService.showSuccess("Tâche supprimée");
          this.taskDeleted.emit(this.task.task_id);
          this.close();
        },
        error: () => this.evaService.showError("Erreur lors de la suppression", "deleteTask", ""),
      });
  }

  // --- Navigation prev/next ---
  private updateNavigationIndex() {
    if (!this.task || !this.tasks?.length) {
      this.currentIndex = -1;
      return;
    }
    this.currentIndex = this.tasks.findIndex((t) => t.task_id === this.task.task_id);
  }

  get canGoPrev(): boolean {
    return this.currentIndex > 0;
  }

  get canGoNext(): boolean {
    return this.currentIndex >= 0 && this.currentIndex < this.tasks.length - 1;
  }

  goToPrev() {
    if (!this.canGoPrev) return;
    this.task = this.tasks[this.currentIndex - 1];
    this.loadTaskData();
    this.updateNavigationIndex();
  }

  goToNext() {
    if (!this.canGoNext) return;
    this.task = this.tasks[this.currentIndex + 1];
    this.loadTaskData();
    this.updateNavigationIndex();
  }

  // --- Close ---
  close() {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  onDialogHide() {
    this.visibleChange.emit(false);
  }

  // --- Display helpers ---
  getCategoryName(categoryId: string): string {
    const cat = this.categories.find((c) => c.category_id === categoryId);
    return cat ? cat.name : "";
  }

  getCategoryColor(categoryId: string): string {
    const cat = this.categories.find((c) => c.category_id === categoryId);
    return cat ? cat.color : "var(--surface-300)";
  }

  /** Retourne la première catégorie pour le breadcrumb */
  getFirstCategoryId(): string {
    return this.task?.category_ids?.[0] || "";
  }

  getAssigneeDisplayName(assignee: TaskAssignee): string {
    return getAssigneeName(assignee, this.employees);
  }

  getInitials(name: string): string {
    const parts = name.trim().split(" ");
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  }

  getObjectLinkPreset(link: any): TagPreset {
    const evaKey = OBJECT_TYPE_TO_EVA[link.object_type?.toUpperCase()] ?? link.object_type;
    const base = EVA_OBJECT_TAGS[evaKey] ?? { label: link.object_type, severity: "secondary" as const };
    return { ...base, label: link.object_name || base.label };
  }

  /** Vérifie si un lien objet est verrouillé (non supprimable depuis ce contexte) */
  isLocked(link: TaskObjectLink): boolean {
    return this.lockedObjectLinks.some((l) => l.object_id === link.object_id && l.object_type === link.object_type);
  }

  getObjectLinkLabel(link: any): string {
    // Types en majuscules depuis EP-1120 (anciens liens en minuscules)
    switch (link.object_type?.toLowerCase()) {
      case "customer":
        return "Client";
      case "project":
        return "Chantier";
      case "offer":
        return "Offre";
      case "intervention":
        return "Intervention";
      default:
        return link.object_type;
    }
  }

  getObjectLinkIcon(link: any): string {
    switch (link.object_type) {
      case "customer":
        return "heroUsers";
      case "project":
        return "heroBuildingOffice";
      case "offer":
        return "heroDocument";
      case "intervention":
        return "heroWrench";
      default:
        return "heroLink";
    }
  }

  getObjectLinkRoute(link: any): string {
    switch (link.object_type) {
      case "customer":
        return `/customers/${link.object_id}`;
      case "project":
        return `/projects/${link.object_id}`;
      case "offer":
        return `/offers/${link.object_id}`;
      case "intervention":
        return `/interventions/${link.object_id}`;
      default:
        return null;
    }
  }

  getUrgencyLabel(urgency: string): string {
    return TaskUrgencyLabels[urgency as TaskUrgency] || urgency;
  }

  private getUrgencyIconForOption(urgency: string): string {
    switch (urgency) {
      case "URGENT":
        return "heroFlagSolid";
      case "NORMAL":
        return "heroFlag";
      case "LOW":
        return "heroFlag";
      default:
        return "heroFlag";
    }
  }

  // --- Lier à ---
  private initLinkSearch(): void {
    this.linkSearch$
      .pipe(
        debounceTime(300),
        switchMap((query) => {
          if (query.length < 2) {
            this.linkSearchResults = [];
            this.isSearchingLinks = false;
            return EMPTY;
          }
          this.isSearchingLinks = true;
          return this.noteService.searchAttachments(query);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (groups) => {
          this.linkSearchResults = groups || [];
          this.isSearchingLinks = false;
        },
        error: () => {
          this.linkSearchResults = [];
          this.isSearchingLinks = false;
        },
      });
  }

  toggleLinkSearch(): void {
    this.showLinkSearch = !this.showLinkSearch;
    if (!this.showLinkSearch) {
      this.linkSearchQuery = "";
      this.linkSearchResults = [];
    }
  }

  onLinkSearch(value: string): void {
    this.linkSearchQuery = value;
    this.linkSearch$.next(value);
  }

  addObjectLink(objectType: string, objectId: string, label: string): void {
    if (!this.task) return;
    const exists = (this.task.objects_links || []).some(
      (l) => l.object_type === objectType && l.object_id === objectId,
    );
    if (exists) return;
    this.task.objects_links = [
      ...(this.task.objects_links || []),
      { object_type: objectType, object_id: objectId, object_name: label },
    ];
    this.saveField({ objects_links: this.task.objects_links });
    this.showLinkSearch = false;
    this.linkSearchQuery = "";
    this.linkSearchResults = [];
  }

  removeObjectLink(objectType: string, objectId: string): void {
    if (!this.task) return;
    this.task.objects_links = (this.task.objects_links || []).filter(
      (l) => !(l.object_type === objectType && l.object_id === objectId),
    );
    this.saveField({ objects_links: this.task.objects_links });
  }

  getBreadcrumbText(): string {
    const firstCatId = this.getFirstCategoryId();
    const catName = firstCatId ? this.getCategoryName(firstCatId) : "";
    const firstLink = this.task?.objects_links?.[0];
    if (catName && firstLink) {
      return `${catName} / ${this.getObjectLinkLabel(firstLink)}`;
    }
    if (catName) {
      return catName;
    }
    return firstLink ? firstLink.object_name || this.getObjectLinkLabel(firstLink) : "Tâche";
  }
}
